// src/components/instructor/ops/AddWorkModal.js
//
// Pass 49B §19/§20 — the real "+ Add work" action for current-season operations. Creates a
// canonical non-lesson schedule event (bookings collection) via createManualBooking with the
// correct scheduleEntryType + activityType — NO new parallel collection, NO fake guest. A
// lesson is created through normal booking creation, not here. Current season only.

import React, { useMemo, useState } from 'react';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { createManualBooking } from '../../../utils/booking';
import { ADD_WORK_ACTIVITIES, ACTIVITY_LABEL, scheduleEntryTypeForActivity, eligibleForActivity } from '../../../utils/activityModel';
import { wallClockToInstant, zonedDateKey } from '../../../utils/opsTime';
import { resolveOrgTimezone } from '../../../utils/organizationRuntime';

export default function AddWorkModal({ organizationId, organization = null, seasonId, seasonMembers = [], defaultDate, areas = [], serviceLocations = [], meetingPoints = [], defaultOperatingAreaId = '', onClose, onCreated }) {
  const { currentUser, userData } = useAuth() || {};
  // Pass 82C.1 — wall-clock ⇄ instant uses the ORGANIZATION's timezone (was hard-coded Asia/Tokyo). SAS resolves
  // Asia/Tokyo from its org doc → identical behavior; a non-JST tenant converts in its own zone.
  const orgTz = resolveOrgTimezone(organization);
  // Pass 82E — the org operates areas when any are configured; then a new work record REQUIRES an explicit area.
  const areaEnabled = areas.length > 0;
  const [activityType, setActivityType] = useState(ADD_WORK_ACTIVITIES[0]);
  const [staffId, setStaffId] = useState('');
  const [operatingAreaId, setOperatingAreaId] = useState(defaultOperatingAreaId || '');
  const [serviceLocationId, setServiceLocationId] = useState('');
  const [date, setDate] = useState(defaultDate || zonedDateKey(new Date(), orgTz));
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('10:00');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Service locations for the selected area (active only) — reset the choice when the area changes.
  const areaLocations = useMemo(
    () => serviceLocations.filter((l) => l.operatingAreaId === operatingAreaId && l.status !== 'inactive'),
    [serviceLocations, operatingAreaId],
  );

  // Offer only staff capable of the selected activity (driving→driving-capable, etc.).
  const staff = useMemo(
    () => eligibleForActivity(seasonMembers, activityType, seasonId),
    [seasonMembers, activityType, seasonId],
  );

  const submit = async () => {
    setError('');
    // §4 the selected person MUST be in the eligibility-filtered `staff` list — no fallback to
    // the full roster, so stale/manipulated select state can never bypass capability checks.
    const member = staff.find((m) => m.userId === staffId);
    if (!staffId) { setError('Choose a team member.'); return; }
    if (!member) { setError('That team member is not eligible for this activity.'); return; }
    if (areaEnabled && !operatingAreaId) { setError('Choose an operating area.'); return; }
    // Pass 82C.1 — build the instants in the ORGANIZATION's wall-clock timezone, never the browser timezone.
    const startAt = wallClockToInstant(date, start, orgTz);
    const endAt = wallClockToInstant(date, end, orgTz);
    if (!startAt || !endAt || !(endAt.getTime() > startAt.getTime())) { setError('Finish must be after start.'); return; }
    setBusy(true);
    try {
      await createManualBooking(db, {
        organizationId,
        organization,
        seasonId,
        instructorId: member.userId,
        instructorName: member.name || member.displayName || member.userId,
        startAt,
        endAt,
        timezone: orgTz,
        // §20 canonical representation — entry type derived from the activity, no new collection,
        // and NO requesterName (never a fake guest for non-lesson work).
        scheduleEntryType: scheduleEntryTypeForActivity(activityType),
        activityType,
        locationLabel: location,
        // Pass 82E — structured operating area + service location; enforce area only when the org operates areas.
        operatingAreaId,
        serviceLocationId,
        enforceOperatingArea: areaEnabled,
        notes,
        actorUserId: currentUser?.uid || userData?.uid || '',
        actorName: userData?.name || currentUser?.displayName || '',
        // Pass 57C (item 12): normalized authorization for Add Work.
        userData,
      });
      onCreated && onCreated();
      onClose && onClose();
    } catch (e) {
      setError(e?.message || 'Could not add work.');
    } finally {
      setBusy(false);
    }
  };

  const field = { display: 'block', width: '100%', padding: '7px 9px', borderRadius: 7, border: '1px solid #C8C5BE', fontSize: 13, marginTop: 4 };
  const label = { fontSize: 11, fontWeight: 600, color: '#6B6860', textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div role="dialog" aria-label="Add work" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, width: 420, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Add work</h3>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#9E9B95' }}>×</button>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B6860' }}>Non-lesson operational work. For a client lesson, create a booking instead.</p>

        <label style={label}>Activity
          <select data-testid="addwork-activity" value={activityType} onChange={(e) => { setActivityType(e.target.value); setStaffId(''); }} style={field}>
            {ADD_WORK_ACTIVITIES.map((a) => <option key={a} value={a}>{ACTIVITY_LABEL[a]}</option>)}
          </select>
        </label>

        <label style={{ ...label, display: 'block', marginTop: 12 }}>Team member
          <select data-testid="addwork-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)} style={field}>
            <option value="">{staff.length ? 'Choose…' : 'No capable staff for this activity'}</option>
            {staff.map((m) => <option key={m.userId} value={m.userId}>{m.name || m.displayName || m.userId}</option>)}
          </select>
        </label>

        {areaEnabled && (
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <label style={{ ...label, flex: 1 }}>Operating area
              <select data-testid="addwork-area" value={operatingAreaId} onChange={(e) => { setOperatingAreaId(e.target.value); setServiceLocationId(''); }} style={field}>
                <option value="">Choose area…</option>
                {areas.filter((a) => a.status !== 'inactive').map((a) => <option key={a.operatingAreaId} value={a.operatingAreaId}>{a.name}</option>)}
              </select>
            </label>
            <label style={{ ...label, flex: 1 }}>Service location
              <select data-testid="addwork-location" value={serviceLocationId} onChange={(e) => setServiceLocationId(e.target.value)} style={field} disabled={!operatingAreaId}>
                <option value="">{areaLocations.length ? 'Optional…' : 'None configured'}</option>
                {areaLocations.map((l) => <option key={l.serviceLocationId} value={l.serviceLocationId}>{l.name}{l.transportRequired ? ' · transport' : ''}</option>)}
              </select>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <label style={{ ...label, flex: 1.4 }}>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} /></label>
          <label style={{ ...label, flex: 1 }}>Start<input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={field} /></label>
          <label style={{ ...label, flex: 1 }}>End<input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={field} /></label>
        </div>

        <label style={{ ...label, display: 'block', marginTop: 12 }}>Location<input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Meeting / pickup point" style={field} /></label>
        <label style={{ ...label, display: 'block', marginTop: 12 }}>Notes<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></label>

        {error ? <div style={{ color: '#DC2626', fontSize: 12, marginTop: 10 }}>{error}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #C8C5BE', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button data-testid="addwork-submit" onClick={submit} disabled={busy} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? 'Adding…' : 'Add work'}</button>
        </div>
      </div>
    </div>
  );
}
